import type { CancelTokenSource } from 'axios';
import { parsePhoneNumberWithError, type CountryCode } from 'libphonenumber-js';
import { create } from 'zustand';

import { requiresNationalId, REFERRALS_ALLOWLISTED_COUNTRY_CODES } from 'constants/restrictedCountries';
import {
    DOCUMENT_CLOSED_WHILE_CONNECTED,
    EMAIL_SESSION_STORAGE_KEY,
    OAUTH_PREFILL_FIRST_NAME,
    OAUTH_PREFILL_LAST_NAME,
    PREFILL_DOB,
    PREFILL_FIRST_NAME,
    PREFILL_LAST_NAME,
    PREFILL_PHONE_NUMBER,
    PRESIGNUP_STARTED_SESSION_STORAGE_KEY,
    PRESIGNUP_TOKEN_SESSION_STORAGE_KEY,
} from 'constants/session';
import type { FacebookPixelTrackerPayload } from 'lib/facebookPixel';
import { loadSocure, socureExecuteOnLoad } from 'lib/socureDocv';
import { setStatsigUserId } from 'lib/statsig';
import { useAuthStore } from 'lib/stores/authStore';
import { useRegistrationSessionStore } from 'lib/stores/registrationSessionStore';
import type { ReferralSignupInfoVo } from 'models/referrals';
import type {
    AdvanceSignUpResponse,
    ExecuteKycRequest,
    ExecuteKycResponse,
    MfaVerificationResponse,
    SsnCollectionMode,
    VerifySignupEmailResponse,
} from 'models/reqres';
import {
    isRiskosIdentityPrefillOtp,
    isRiskosIdentityPrefillSsn4,
    isRiskosIdentityPrefillStart,
    isRiskosProfileReview,
    selectLatestPrefillAddress,
    type RiskosPrefillSuggestion,
    type SignUpAdvanceRequest,
    type SignupAction,
} from 'models/riskos';
import type { SignupVo } from 'models/signup';
import type {
    ActivationStep,
    DigitalKycResult,
    DocumentKycResult,
    KycStatus,
    ReasonDocumentKycRetry,
    SignupStep,
} from 'models/signup';
import type { SocureConfigV5 } from 'models/socure';
import { getCountryCodeSnapshot } from 'queries/countryCode';
import { setSsnCollectionMode as setSsnStatusCache } from 'queries/ssn';
import { useAttributionStore, type UTM } from 'stores/zustand/attributionStore';
import { useIdentityFlowDialogStore } from 'stores/zustand/identityFlowDialogStore';
import { handleSnackbarError, useSnackbarStore } from 'stores/zustand/snackbarStore';
import analyticsProvider, { hashUserEmail } from 'utils/analytics/analyticsProvider';
import { identify as amplitudeIdentify } from 'utils/analytics/providers/amplitude';
import { clearAttributionHistory, getAttributionHistory, resolveUtmDataForSubmission } from 'utils/attribution';
import { GrowthEvent, growthEventId } from 'utils/growthEventId';
import interactionResponse from 'utils/interaction-response';
import { clearKycFlowId, trackKycStep } from 'utils/kycTracking';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { getReferralSignupParam, resolveReferralForSubmission } from 'utils/referralAttribution';
import { getRiskifiedSessionId } from 'utils/riskified';
import { fetchSardineSessionKey, peekSardineSessionKey, waitForSardineBound } from 'utils/sardine';
import { setSprigUserId } from 'utils/sprig';
import {
    getSessionStorageItem,
    removeLocalStorageItem,
    removeSessionStorageItem,
    setSessionStorageItem,
} from 'utils/storage';
import { uuidv4 } from 'utils/uuid';

import type { ServerError } from '../../models';
import type { PollingFrequency } from '../../transport';
import { clearCsrfToken, exchangeApi } from '../../transport/core';
import { mfaVerification } from '../../transport/domains/mfa';
import { getProfile, setUserAttribution, updateUserInfo } from '../../transport/domains/profile';
import {
    executeDigitalKyc,
    executeDocumentVerification,
    getLocation,
    getUserSignup,
    sendSignupEmailVerification as sendSignupEmailVerificationTransport,
    sendSignupLink,
    signUp,
    signUpAdvance,
    signUpVerified,
    verifyPhone as verifyPhoneTransport,
    verifySignupEmail as verifySignupEmailTransport,
} from '../../transport/domains/signup';
import config from '../../utils/config';
import { useReferralStore } from './referralStore';

const noop = (): null => null;

const EMPTY_UTM: UTM = {
    utm_content: undefined,
    utm_source: undefined,
    utm_campaign: undefined,
    utm_term: undefined,
    utm_medium: undefined,
    utm_landing: undefined,
};

const REGEX_ALL_NON_DIGITS = /[^0-9]/g;
export const enum FundMethods {
    BANK = 'bank',
    WIRE = 'wire',
    DEBIT_CARD = 'debit_card',
    CRYPTO = 'crypto',
    BRAINTREE_PAYPAL = 'braintree_paypal',
    BRAINTREE_VENMO = 'braintree_venmo',
    CASHAPP = 'cashapp',
}

// Every Web Storage access in this file goes through utils/storage, which
// resolves the global defensively and swallows the SecurityError that a
// sandboxed iframe or a browser configured to block site data raises. That
// matters most on the teardown paths below: they run in the middle of
// `resetAppStateOnLogout`, so an escaping exception would skip the in-memory
// disposal and every later logout step.
/** As `getSessionStorageItem`, but '' rather than null — the store's fields are strings. */
function readSessionItem(key: string): string {
    return getSessionStorageItem(key) ?? '';
}

/** Bulk erase, for the teardown paths that drop a whole group of keys at once. */
function removeStoredItems(kind: 'local' | 'session', keys: string[]): void {
    const remove = kind === 'local' ? removeLocalStorageItem : removeSessionStorageItem;
    for (const key of keys) {
        remove(key);
    }
}

// Deploy migration. The release before this one persisted the whole flow decision
// here, which is what let a client force the legacy path; applicants who were mid
// pre-signup flow when this shipped carry only this key. Delete it (and the reads
// below) once no tab opened before the release can still be around — sessionStorage
// dies with the tab, so a few days is plenty.
const LEGACY_PRESIGNUP_FLOW_SESSION_STORAGE_KEY = 'presignupEmailVerificationFlow';

/**
 * Whether a pre-signup flow is already under way in this tab, from the one-way resume
 * marker. Only ever answers "yes": a missing marker leaves the decision unmade for the
 * gate to settle, and the legacy key's `false` — the value the forcing bug wrote — is
 * ignored, so client storage can restore the pre-signup path but never select the
 * legacy one.
 */
function hasStartedPresignupFlow(): boolean {
    return (
        !!readSessionItem(PRESIGNUP_STARTED_SESSION_STORAGE_KEY) ||
        readSessionItem(LEGACY_PRESIGNUP_FLOW_SESSION_STORAGE_KEY) === 'true'
    );
}

/** Both keys `hasStartedPresignupFlow` reads, so a finished flow cannot be resumed. */
const PRESIGNUP_RESUME_MARKER_KEYS = [PRESIGNUP_STARTED_SESSION_STORAGE_KEY, LEGACY_PRESIGNUP_FLOW_SESSION_STORAGE_KEY];

export interface PendingSmsMarketingConsent {
    userId: string;
    phoneE164: string;
    contentHash: string;
    shownDefault: 'checked' | 'unchecked';
}

interface RegistrationState {
    // --- registration fields ---
    email: string;
    password: string;
    passwordConfirmation: string;
    firstName: string;
    lastName: string;
    sandboxEmailValidationCode: string | undefined;
    address1: string;
    address2: string;
    postalCode: string;
    taxId: string;
    city: string;
    state: string;
    country: string;
    birthdate: Date | undefined;
    dayOfBirth: string;
    monthOfBirth: string;
    yearOfBirth: string;
    countryCode: string;
    areaCode: string;
    phoneNumber: string;
    rawPhoneInput: string;
    pendingSmsMarketingConsent: PendingSmsMarketingConsent | undefined;
    detectedCountryCode: string;
    prefillAttemptId: string;
    documentsReviewed: boolean[];
    agreementCheckboxes: boolean[];
    fundMethod: FundMethods | undefined;
    loading: boolean;
    loaded: boolean;
    referralSignupInfo: ReferralSignupInfoVo | undefined;
    signupSardineSessionKey: string;
    // Minted by pre-signup verification; consumed by signupVerified.
    emailVerificationToken: string;
    // Gate decision pinned at flow entry so a mid-flow change can't strand a user.
    // undefined = not yet evaluated; steps re-pin it from the gate on a refresh (see
    // usePinnedPresignupEmailVerification), and a started pre-signup flow restores
    // itself through PRESIGNUP_STARTED_SESSION_STORAGE_KEY. The legacy path is never
    // restored from storage — that direction is what the enumeration bug exploited.
    presignupEmailVerificationEnabled: boolean | undefined;

    // --- kyc fields (flattened; `loading`/`load`/`dispose` renamed to avoid
    // colliding with the registration fields/actions of the same name) ---
    documentVerificationStatus: KycStatus;
    documentVerificationResult: DocumentKycResult;
    digitalVerificationStatus: KycStatus;
    digitalVerificationResult: DigitalKycResult;
    ssnCollectionMode: SsnCollectionMode;
    activationStep: ActivationStep;
    lastSignupStep: SignupStep;
    signupAction: SignupAction | undefined;
    advanceInFlight: boolean;
    duplicateEmail: string;
    duplicateOAuthProvider: string;
    duplicateAccessLevel: string;
    duplicateAccountIsRecoverable: boolean;
    recoveryCode: string;
    recoveryExistingUserId: string;
    reasonDocumentRetry: ReasonDocumentKycRetry;
    documentVerificationErrorMessage: string;
    kycLoading: boolean;
    submittingDocument: boolean;
    isConnectedOnMobile: boolean;
    closedWhileConnected: boolean;
    startedSocure: boolean;
    displaySocure: boolean;
    socureLoadError: boolean;
    polling: boolean;
    docvTransactionToken: string | null;
    deviceSessionToken: string | null;

    actions: RegistrationActions;
}

/**
 * Released when a request is abandoned because the registration session ended between
 * it being issued and its response arriving.
 *
 * The actions below suppress `onSuc`/`onErr`/`onFinally` on that path on purpose: those
 * handlers navigate, toast and write signup state, which is exactly what must not happen
 * for a session that is gone. But callers raise a spinner *before* calling in
 * (`setVerifying(true)`, `setContinuing(true)`) and clear it only from those same
 * handlers — so suppressing all of them latches the submit button until a reload.
 * `onAborted` is the one callback that still fires, for that and only that.
 *
 * Implementations must therefore release local UI state and nothing else: no navigation,
 * no snackbar, no session or store writes. A different applicant may own this tab by the
 * time it runs, and moving or messaging them is the bug the generation guards exist to
 * prevent. Mirrors `OnLoginAborted` in `lib/stores/userSessionStore`, which carries the
 * same contract for the login entry points.
 */
type OnRegistrationAborted = () => void;

interface RegistrationActions {
    // --- registration actions ---
    setEmailWithoutSession: (newValue: string) => void;
    setEmail: (newValue: string) => void;
    setPassword: (newValue: string) => void;
    setPasswordConfirmation: (newValue: string) => void;
    setFirstName: (newValue: string) => void;
    setLastName: (newValue: string) => void;
    setAddress1: (newValue: string) => void;
    setAddress2: (newValue: string) => void;
    setPostalCode: (newValue: string) => void;
    setTaxId: (newValue: string) => void;
    setCity: (newValue: string) => void;
    setState: (newValue: string) => void;
    setCountry: (newValue: string) => void;
    setCountryCode: (newValue: string) => void;
    setDetectedCountryCode: (newValue: string) => void;
    loadDetectedCountry: () => void;
    setUserId: (newValue: string) => void;
    setBirthdate: (newValue: Date | undefined) => void;
    setDayOfBirth: (newValue: string) => void;
    setMonthOfBirth: (newValue: string) => void;
    setYearOfBirth: (newValue: string) => void;
    setPhoneNumber: (newValue: string) => void;
    setPendingSmsMarketingConsent: (choice: PendingSmsMarketingConsent | undefined) => void;
    takePendingSmsMarketingConsent: () => PendingSmsMarketingConsent | undefined;
    parsePhoneNumberParts: () => void;
    setPrefillAttemptId: (newValue: string) => void;
    hydrateFromPrefillSuggestion: (suggestion: RiskosPrefillSuggestion) => void;
    setDocumentsReviewed: (newValue: boolean[]) => void;
    setAgreementCheckBoxes: (newValue: boolean[]) => void;
    setFundMethod: (newValue: FundMethods | undefined) => void;
    trackSuccessfulSignUp: (signupUserId?: string) => void;
    sendAttributionHistory: (userId: string) => void;
    /**
     * `onAborted` sits ahead of `efc` so this stays positionally interchangeable with
     * {@link signupVerified} — `useSignUp` picks between the two by flow and calls the
     * result through one binding. `efc` has no caller today; it trails for that reason.
     */
    signup: (
        onSuc: (userId: string, sardineSessionKey: string) => void,
        onErr: (err: ServerError) => void,
        onFinally?: () => void,
        onAborted?: OnRegistrationAborted,
        efc?: boolean,
    ) => Promise<void>;
    /** Pins the flow decision for the session; `gateEnabled` is the Statsig gate value read at the call site. */
    pinPresignupEmailVerification: (gateEnabled: boolean) => boolean;
    setEmailVerificationToken: (newValue: string) => void;
    sendSignupEmailVerification: (
        onSuc: () => void,
        onErr: (err: ServerError) => void,
        onFinally?: () => void,
        onAborted?: OnRegistrationAborted,
    ) => void;
    verifySignupEmail: (
        code: string,
        onSuc: (resp: VerifySignupEmailResponse) => void,
        onErr: (err: ServerError) => void,
        onFinally?: () => void,
        onAborted?: OnRegistrationAborted,
    ) => void;
    signupVerified: (
        onSuc: (userId: string, sardineSessionKey: string) => void,
        onErr: (err: ServerError) => void,
        onFinally?: () => void,
        onAborted?: OnRegistrationAborted,
    ) => Promise<void>;
    sendResumeLink: (email: string, onSuc: () => void) => void;
    load: (onSuc?: (signup: SignupVo) => void, onErr?: () => void) => void;
    loadProfile: (onSuc?: () => void, onFinally?: () => void) => void;
    submit: (onSuc?: () => void, onErr?: (error: ServerError) => void) => void;
    initiateMfaVerificationForPhone: (onSuc: () => void, onErr: () => void) => void;
    verifyPhone: (code: string, onSuc: (duplicateEmail?: string) => void, onErr: (error: ServerError) => void) => void;
    endSession: () => void;
    clearStoredState: () => void;
    dispose: () => void;
    // --- kyc actions ---
    updateFromVo: (signup: SignupVo) => void;
    setSsnCollectionMode: (mode: SsnCollectionMode) => void;
    setSignupAction: (action: SignupAction | undefined) => void;
    signUpAdvance: (
        payload: SignUpAdvanceRequest,
        onSuc?: (response: AdvanceSignUpResponse) => void,
        onErr?: (err: ServerError) => void,
        onFinally?: () => void,
    ) => void;
    updateExtraFields: (response: ExecuteKycResponse) => void;
    loadKyc: (
        polling?: PollingFrequency,
        onSuc?: (signup: SignupVo) => void,
        onErr?: () => void,
        onFinally?: () => void,
        isPoll?: boolean,
        userId?: string,
    ) => void;
    executeDigitalKyc: (
        socureDeviceSessionId: string | null,
        onSuc?: () => void,
        onErr?: (err: ServerError) => void,
        onFinally?: () => void,
    ) => Promise<void>;
    setDuplicateEmail: (newValue: string) => void;
    setDuplicateOAuthProvider: (newValue: string) => void;
    setDuplicateAccessLevel: (newValue: string) => void;
    setRecoveryParams: (code: string, existingUserId: string) => void;
    clearRecoveryParams: () => void;
    setIsConnectedOnMobile: (value: boolean) => void;
    setClosedWhileConnected: (value: boolean) => void;
    setStartedSocure: (value: boolean) => void;
    setDisplaySocure: (value: boolean) => void;
    setReasonDocumentRetry: (value: ReasonDocumentKycRetry) => void;
    setDocumentVerificationErrorMessage: (value: string) => void;
    // Resolves false when the flow could not be started at all (the docv token request
    // failed), so callers can surface an error instead of leaving the user on a spinner.
    initSocure: () => Promise<boolean>;
    resetSocure: () => void;
    // Drop the outcome of the user's last document check, so the state describes the check
    // about to run instead of one that already finished.
    resetDocumentVerification: () => void;
    executeDocumentKyc: (
        documentId: string,
        onSuc?: () => void,
        onErr?: (err: ServerError) => void,
        onFinally?: () => void,
    ) => void;
    startPolling: (slow?: boolean) => void;
    stopPolling: () => void;
    disposeKyc: () => void;
}

// Module-scoped (non-reactive) implementation details for fields that are
// only ever read/written internally, never by a consumer. Kept out of the
// store so setting them doesn't trigger re-renders.
let attributionSendInFlight = false;
let kycTimer: ReturnType<typeof setInterval> | null = null;
let kycFetchRef: CancelTokenSource | null = null;
let profileFetchRef: CancelTokenSource | null = null;
// Bumped by dispose(). Any request that started before the session ended must not
// apply its callbacks afterwards: cancellation alone can't unwind a response
// already resolved into the microtask queue, and these handlers write the
// previous applicant's profile and signup state — recovery data included — back
// into the store and into sessionStorage. Every async continuation that outlives
// a request captures this and bails when it no longer matches.
let sessionGeneration = 0;

/**
 * Snapshot of the registration session in progress, for flows that own async work
 * this store cannot reach — `DuplicateAccountRecovery` runs its own DocV token
 * fetch and recovery submission. Capture this before starting such work and check
 * {@link isRegistrationSessionCurrent} in every continuation, so a logout in
 * between can't let the ended applicant's response relaunch verification or
 * persist their password-reset code for whoever is in this tab next.
 */
export const captureRegistrationSession = (): number => sessionGeneration;

export const isRegistrationSessionCurrent = (generation: number): boolean => generation === sessionGeneration;

export const useRegistrationStore = create<RegistrationState>()((set, get) => ({
    email: readSessionItem(EMAIL_SESSION_STORAGE_KEY),
    password: '',
    passwordConfirmation: '',
    firstName: '',
    lastName: '',
    sandboxEmailValidationCode: undefined,
    address1: '',
    address2: '',
    postalCode: '',
    taxId: '',
    city: '',
    state: '',
    country: 'US',
    birthdate: undefined,
    dayOfBirth: '',
    monthOfBirth: '',
    yearOfBirth: '',
    countryCode: '',
    areaCode: '',
    phoneNumber: '',
    rawPhoneInput: '',
    pendingSmsMarketingConsent: undefined,
    detectedCountryCode: '',
    prefillAttemptId: '',
    documentsReviewed: [false, false, false, false],
    agreementCheckboxes: [false],
    fundMethod: undefined,
    loading: false,
    loaded: false,
    referralSignupInfo: undefined,
    signupSardineSessionKey: '',
    emailVerificationToken: readSessionItem(PRESIGNUP_TOKEN_SESSION_STORAGE_KEY),
    // Storage may only ever restore the pre-signup path, never select the legacy one:
    // the marker is written once a verification email has gone out, and its absence
    // leaves the decision unmade rather than pinning `false`.
    presignupEmailVerificationEnabled: hasStartedPresignupFlow() ? true : undefined,

    documentVerificationStatus: 'pending',
    documentVerificationResult: 'unknown',
    digitalVerificationStatus: 'pending',
    digitalVerificationResult: 'unknown',
    ssnCollectionMode: 'none',
    activationStep: 'verify-email',
    lastSignupStep: 'account-created',
    signupAction: undefined,
    advanceInFlight: false,
    duplicateEmail: readSessionItem('recovery_duplicate_email'),
    duplicateOAuthProvider: readSessionItem('recovery_duplicate_oauth_provider'),
    duplicateAccessLevel: '',
    duplicateAccountIsRecoverable: readSessionItem('recovery_duplicate_account_is_recoverable') === 'true',
    recoveryCode: readSessionItem('recovery_reset_code'),
    recoveryExistingUserId: readSessionItem('recovery_existing_user_id'),
    reasonDocumentRetry: '',
    documentVerificationErrorMessage: '',
    kycLoading: false,
    submittingDocument: false,
    isConnectedOnMobile: false,
    closedWhileConnected: readSessionItem(DOCUMENT_CLOSED_WHILE_CONNECTED) === 'true',
    startedSocure: false,
    displaySocure: false,
    socureLoadError: false,
    polling: false,
    docvTransactionToken: null,
    deviceSessionToken: null,

    actions: {
        setEmailWithoutSession: (newValue) => set({ email: newValue }),
        setEmail: (newValue) => {
            set({ email: newValue });
            setSessionStorageItem(EMAIL_SESSION_STORAGE_KEY, newValue);
        },
        setPassword: (newValue) => set({ password: newValue }),
        setPasswordConfirmation: (newValue) => set({ passwordConfirmation: newValue }),
        setFirstName: (newValue) => set({ firstName: newValue }),
        setLastName: (newValue) => set({ lastName: newValue }),
        setAddress1: (newValue) => set({ address1: newValue }),
        setAddress2: (newValue) => set({ address2: newValue }),
        setPostalCode: (newValue) => {
            // Only strip non-digits for US ZIP codes, international postal codes can have letters
            if (get().country === 'US') {
                set({ postalCode: newValue.replace(REGEX_ALL_NON_DIGITS, '') });
            } else {
                set({ postalCode: newValue });
            }
        },
        setTaxId: (newValue) => set({ taxId: newValue }),
        setCity: (newValue) => set({ city: newValue }),
        setState: (newValue) => set({ state: newValue.trim().toUpperCase() }),
        setCountry: (newValue) => set({ country: newValue }),
        setCountryCode: (newValue) => set({ countryCode: newValue }),
        setDetectedCountryCode: (newValue) => set({ detectedCountryCode: newValue }),
        loadDetectedCountry: () => {
            // Avoid multiple calls - only fetch if not already set
            if (get().detectedCountryCode) {
                return;
            }
            getLocation(
                (location) => {
                    if (location?.country_iso) {
                        get().actions.setDetectedCountryCode(location.country_iso);
                    }
                },
                () => {
                    // On error, detectedCountryCode stays empty, getPhoneDefaultCountry returns 'US'
                },
            );
        },
        setUserId: (newValue) => {
            useRegistrationSessionStore.getState().actions.userIdAssigned(newValue);
            logProviderFactory.setUser(newValue);
            amplitudeIdentify(newValue, {
                country_code: get().detectedCountryCode,
            });
            setSprigUserId(newValue);
            // Early-identify to Statsig so signup exposures carry a valid userID (authStore lags here).
            void setStatsigUserId(newValue);
        },
        setBirthdate: (newValue) => set({ birthdate: newValue }),
        setDayOfBirth: (newValue) => set({ dayOfBirth: newValue }),
        setMonthOfBirth: (newValue) => set({ monthOfBirth: newValue }),
        setYearOfBirth: (newValue) => set({ yearOfBirth: newValue }),
        setPhoneNumber: (newValue) => {
            // Just store the raw input - AsYouType formatter handles display.
            set({ rawPhoneInput: newValue });
        },
        setPendingSmsMarketingConsent: (choice) => set({ pendingSmsMarketingConsent: choice }),
        takePendingSmsMarketingConsent: () => {
            const choice = get().pendingSmsMarketingConsent;
            set({ pendingSmsMarketingConsent: undefined });
            return choice;
        },
        parsePhoneNumberParts: () => {
            try {
                const parsed = parsePhoneNumberWithError(get().rawPhoneInput, selectPhoneDefaultCountry(get()));
                const countryCode = parsed.countryCallingCode;
                // For NANP (+1) numbers, split into area code + phone number
                if (parsed.countryCallingCode === '1' && parsed.nationalNumber.length === 10) {
                    set({
                        countryCode,
                        areaCode: parsed.nationalNumber.substring(0, 3),
                        phoneNumber: parsed.nationalNumber.substring(3),
                    });
                } else {
                    // For international numbers, store full national number with empty area code
                    set({ countryCode, areaCode: '', phoneNumber: parsed.nationalNumber });
                }
            } catch {
                // If parsing fails, clear parts - validation will catch this
                set({ countryCode: '', areaCode: '', phoneNumber: '' });
            }
        },
        setPrefillAttemptId: (newValue) => set({ prefillAttemptId: newValue }),
        hydrateFromPrefillSuggestion: (suggestion) => {
            const { actions } = get();
            if (suggestion.first_name) {
                actions.setFirstName(suggestion.first_name);
            }
            if (suggestion.last_name) {
                actions.setLastName(suggestion.last_name);
            }
            if (suggestion.birth_date && suggestion.birth_date.length >= 10) {
                actions.setYearOfBirth(suggestion.birth_date.substring(0, 4));
                actions.setMonthOfBirth(suggestion.birth_date.substring(5, 7));
                actions.setDayOfBirth(suggestion.birth_date.substring(8, 10));
            }
            const address = selectLatestPrefillAddress(suggestion);
            if (address) {
                actions.setAddress1(address.street1);
                actions.setAddress2(address.street2 ?? '');
                actions.setCity(address.city);
                actions.setState(address.state);
                actions.setPostalCode(address.postal_code);
                actions.setCountry(address.country || 'US');
            }
        },
        setDocumentsReviewed: (newValue) => set({ documentsReviewed: newValue }),
        setAgreementCheckBoxes: (newValue) => set({ agreementCheckboxes: newValue }),
        setFundMethod: (newValue) => set({ fundMethod: newValue }),
        trackSuccessfulSignUp: (signupUserId) => {
            // Prefer the freshly-returned signup user_id; setUserId has not run yet at this point.
            const userId = signupUserId || useAuthStore.getState().userId || '';
            // Push the real identity to the dataLayer before SignupProfile so the Addressable
            // registration conversion can read external_id/em (the GTM Data Layer Variables).
            if (typeof window !== 'undefined') {
                window.dataLayer = window.dataLayer || [];
                if (userId) {
                    window.dataLayer.push({ external_id: userId, is_registered_user: true });
                }
                const { email } = get();
                if (email) {
                    window.dataLayer.push({ em: hashUserEmail(email) });
                }
            }
            const trackerPayload: FacebookPixelTrackerPayload = {
                page: window.location.pathname,
                event_id: userId ? growthEventId(userId, GrowthEvent.Signup) : uuidv4(),
            };
            // For Amplitude, this is replaced by recordSignUpMetric, but we still need it for GTM tags
            analyticsProvider.sendEvent('SignupProfile', trackerPayload);
        },
        sendAttributionHistory: (userId) => {
            // Send attribution history if available (called on both signup and login).
            // Guard against concurrent calls (e.g. handleLoginSuccess + explicit caller) racing
            // to submit the same history twice before the success callback clears localStorage.
            if (attributionSendInFlight) return;
            if (userId && typeof window !== 'undefined') {
                const history = getAttributionHistory();
                if (history.length > 0) {
                    attributionSendInFlight = true;
                    // dispose() lowers the guard so a new session can submit, which means
                    // this request can still be pending while the next one runs. Both
                    // callbacks below are therefore scoped to the session that started
                    // them: a late success must not clearAttributionHistory() — that
                    // localStorage now holds the *new* applicant's touchpoints — and
                    // neither callback may lower a guard it no longer owns, which would
                    // let a third call submit alongside the live request.
                    const generation = sessionGeneration;
                    setUserAttribution(
                        userId,
                        history,
                        () => {
                            if (generation !== sessionGeneration) return;
                            // Clear localStorage after successful send
                            clearAttributionHistory();
                            attributionSendInFlight = false;
                        },
                        (err) => {
                            if (generation !== sessionGeneration) return;
                            console.warn('Failed to send attribution history:', err);
                            // Keep the data in localStorage for later retry
                            attributionSendInFlight = false;
                        },
                    );
                }
            }
        },
        signup: async (onSuc, onErr, onFinally = noop, onAborted = noop, efc) => {
            const { email, password, actions } = get();
            setSessionStorageItem(EMAIL_SESSION_STORAGE_KEY, email);
            // Captured before the Sardine wait, and re-checked before the request and
            // in its success handler. onSuc runs useSignUp's continuation, which opens
            // the auto-login with the email/password captured above; stopping it here
            // keeps a torn-down session from ever reaching that point. (The login
            // itself is separately invalidated by userSessionStore's loginGeneration,
            // which covers a teardown landing after this handler has already run.)
            const generation = sessionGeneration;

            const sardine = await fetchSardineSessionKey('signup');
            if (!sardine.ok || generation !== sessionGeneration) {
                onAborted();
                return;
            }
            const sardineSessionKey = sardine.key;
            set({ signupSardineSessionKey: sardineSessionKey });
            const { referral, invalidatedReferralCode } = useReferralStore.getState();
            const referralCode = resolveReferralForSubmission(referral, invalidatedReferralCode);

            signUp(
                {
                    email,
                    password,
                    initial_referrer: document.referrer,
                    efc: efc,
                    skip_verification_email: false,
                    sardine_session_key: sardineSessionKey || undefined,
                    ...resolveUtmDataForSubmission(useAttributionStore.getState().utmData ?? EMPTY_UTM),
                },
                async (response) => {
                    if (generation !== sessionGeneration) return;
                    set({ sandboxEmailValidationCode: response.code });
                    await interactionResponse();
                    if (generation !== sessionGeneration) return;
                    actions.trackSuccessfulSignUp(response.user_id);
                    onSuc(response.user_id, sardineSessionKey);
                },
                (err) => {
                    if (generation !== sessionGeneration) return;
                    onErr(err);
                },
                // Always-run slot, so the abort is reported here — exactly once per
                // request. See OnRegistrationAborted: the account may well exist by
                // now; only the caller's continuation (the auto-login) was abandoned.
                () => {
                    if (generation !== sessionGeneration) {
                        onAborted();
                        return;
                    }
                    onFinally();
                },
                // Referral only for allowlisted (US + a few) countries
                getReferralSignupParam(referralCode, useAuthStore.getState().user?.country || getCountryCodeSnapshot()),
            );
        },
        pinPresignupEmailVerification: (gateEnabled) => {
            const pinned = get().presignupEmailVerificationEnabled;
            if (pinned !== undefined) {
                return pinned;
            }
            set({ presignupEmailVerificationEnabled: gateEnabled });
            return gateEnabled;
        },
        setEmailVerificationToken: (newValue) => {
            set({ emailVerificationToken: newValue });
            if (newValue) {
                setSessionStorageItem(PRESIGNUP_TOKEN_SESSION_STORAGE_KEY, newValue);
            } else {
                removeSessionStorageItem(PRESIGNUP_TOKEN_SESSION_STORAGE_KEY);
            }
        },
        sendSignupEmailVerification: (onSuc, onErr, onFinally = noop, onAborted = noop) => {
            const { email } = get();
            setSessionStorageItem(EMAIL_SESSION_STORAGE_KEY, email);
            const generation = sessionGeneration;
            sendSignupEmailVerificationTransport(
                { email },
                (response) => {
                    if (generation !== sessionGeneration) return;
                    // The flow is now under way: a code is in the applicant's inbox. Mark it
                    // so a reload can resume it even if the rollout is switched off meanwhile.
                    setSessionStorageItem(PRESIGNUP_STARTED_SESSION_STORAGE_KEY, 'true');
                    set({ sandboxEmailValidationCode: response.code });
                    onSuc();
                },
                (err) => {
                    if (generation !== sessionGeneration) return;
                    onErr(err);
                },
                // The transport always runs this slot, so it is where the abort is
                // reported — exactly once per request, whichever of the two handlers
                // above was the one suppressed. See OnRegistrationAborted.
                () => {
                    if (generation !== sessionGeneration) {
                        onAborted();
                        return;
                    }
                    onFinally();
                },
            );
        },
        verifySignupEmail: (code, onSuc, onErr, onFinally = noop, onAborted = noop) => {
            const { email, actions } = get();
            // dispose() clears `emailVerificationToken`; a response landing after it
            // would restore the previous applicant's verification credential for the
            // next signup in this tab to submit.
            const generation = sessionGeneration;
            verifySignupEmailTransport(
                { email, code },
                (response) => {
                    if (generation !== sessionGeneration) return;
                    if (response.email_verification_token) {
                        actions.setEmailVerificationToken(response.email_verification_token);
                    }
                    onSuc(response);
                },
                (err) => {
                    if (generation !== sessionGeneration) return;
                    onErr(err);
                },
                // See sendSignupEmailVerification: the always-run slot reports the abort.
                () => {
                    if (generation !== sessionGeneration) {
                        onAborted();
                        return;
                    }
                    onFinally();
                },
            );
        },
        signupVerified: async (onSuc, onErr, onFinally = noop, onAborted = noop) => {
            const { email, password, emailVerificationToken, actions } = get();
            setSessionStorageItem(EMAIL_SESSION_STORAGE_KEY, email);
            // See signup(): onSuc logs the applicant in with the credentials captured
            // above, so a teardown between here and the response must stop it.
            const generation = sessionGeneration;

            const sardine = await fetchSardineSessionKey('signup');
            if (!sardine.ok || generation !== sessionGeneration) {
                onAborted();
                return;
            }
            const sardineSessionKey = sardine.key;
            set({ signupSardineSessionKey: sardineSessionKey });
            const { referral, invalidatedReferralCode } = useReferralStore.getState();
            const referralCode = resolveReferralForSubmission(referral, invalidatedReferralCode);

            signUpVerified(
                {
                    email,
                    password,
                    email_verification_token: emailVerificationToken,
                    initial_referrer: document.referrer,
                    efc: undefined,
                    skip_verification_email: undefined,
                    sardine_session_key: sardineSessionKey || undefined,
                    ...resolveUtmDataForSubmission(useAttributionStore.getState().utmData ?? EMPTY_UTM),
                },
                async (response) => {
                    if (generation !== sessionGeneration) return;
                    // The token is spent once the account exists; dropping it and the resume
                    // markers keeps a later signup in this tab from resuming on either.
                    actions.setEmailVerificationToken('');
                    removeStoredItems('session', PRESIGNUP_RESUME_MARKER_KEYS);
                    await interactionResponse();
                    if (generation !== sessionGeneration) return;
                    actions.trackSuccessfulSignUp(response.user_id);
                    onSuc(response.user_id, sardineSessionKey);
                },
                (err) => {
                    if (generation !== sessionGeneration) return;
                    onErr(err);
                },
                // See signup(): always-run slot, so the abort is reported here.
                () => {
                    if (generation !== sessionGeneration) {
                        onAborted();
                        return;
                    }
                    onFinally();
                },
                // Referral only for allowlisted (US + a few) countries
                getReferralSignupParam(referralCode, useAuthStore.getState().user?.country || getCountryCodeSnapshot()),
            );
        },
        sendResumeLink: (email, onSuc) => {
            sendSignupLink(email, onSuc, handleSnackbarError);
        },
        load: (onSuc = noop, onErr = noop) => {
            const { actions } = get();
            actions.loadDetectedCountry();
            actions.loadProfile(() => {
                actions.loadKyc(undefined, onSuc, onErr);
            });
        },
        loadProfile: (onSuc = noop, onFinally = noop) => {
            set({ loading: true });
            const { actions } = get();
            const generation = sessionGeneration;
            // Declared ahead of the call rather than `const ref = (profileFetchRef =
            // ...)`: the callbacks below close over it, and on that form a transport
            // that ever called back synchronously would hit a TDZ ReferenceError.
            // Reading `undefined` instead just makes the ownership check fail closed.
            let requestRef: CancelTokenSource | undefined;
            profileFetchRef = requestRef = getProfile(
                useRegistrationSessionStore.getState().userId,
                (user) => {
                    if (generation !== sessionGeneration) return;
                    useAuthStore.getState().actions.userUpdated(user);
                    set({ countryCode: user.country_code, areaCode: user.area_code, phoneNumber: user.phone_number });
                    if (user.birth_date && user.birth_date.length >= 10) {
                        actions.setDayOfBirth(user.birth_date.substring(8, 10));
                        actions.setMonthOfBirth(user.birth_date.substring(5, 7));
                        actions.setYearOfBirth(user.birth_date.substring(0, 4));
                    }
                    actions.setCity(user.city);
                    actions.setCountry(user.country || 'US');
                    actions.setEmail(user.email);
                    actions.setFirstName(user.first_name);
                    actions.setLastName(user.last_name);
                    actions.setPostalCode(user.postal_code);
                    actions.setState(user.state);
                    actions.setAddress1(user.street1);
                    actions.setAddress2(user.street2);
                    onSuc();
                },
                noop,
                () => {
                    // Retire only the request this call owns, the way tokenFetch and
                    // notificationFlushCancel do: a second loadProfile() in the same
                    // generation installs its own handle, and nulling that one would
                    // leave dispose() with nothing to cancel.
                    if (profileFetchRef === requestRef) profileFetchRef = null;
                    // A teardown already reset `loading`/`loaded`; re-marking the
                    // ended session as loaded would leave the next signup reading
                    // a profile that was never fetched for it.
                    if (generation !== sessionGeneration) return;
                    set({ loading: false, loaded: true });
                    onFinally();
                },
            );
        },
        submit: (onSuc = noop, onErr = noop) => {
            const {
                city,
                country,
                yearOfBirth,
                monthOfBirth,
                dayOfBirth,
                firstName,
                lastName,
                postalCode,
                state,
                address1,
                address2,
                taxId,
                prefillAttemptId,
            } = get();
            // onSuc is the caller's continuation — ProfileEnterAddress's
            // onAddressSubmitted starts loadKyc() — so a response landing after a
            // teardown would drive the flow with whichever registration user id has
            // since become current.
            const generation = sessionGeneration;
            updateUserInfo(
                useRegistrationSessionStore.getState().userId,
                {
                    city,
                    country,
                    birth_date: `${yearOfBirth}-${monthOfBirth}-${dayOfBirth}`,
                    first_name: firstName,
                    last_name: lastName,
                    postal_code: postalCode,
                    state,
                    street1: address1,
                    street2: address2,
                    use_bid_ask: false,
                    ...(requiresNationalId(country) && {
                        tax_id: taxId.trim(),
                    }),
                    ...(prefillAttemptId ? { prefill_attempt_id: prefillAttemptId } : {}),
                    // Peeked, not fetched: the initial profile submission produces no PII
                    // diff server-side, so the key goes unused — but a KYC retry that
                    // corrects a name or address does get screened, so it is still sent.
                    // executeDigitalKyc needs this same 'kyc' session a step later, which
                    // a consuming fetch would take from it.
                    sardine_session_key: peekSardineSessionKey() ?? undefined,
                },
                () => {
                    if (generation !== sessionGeneration) return;
                    onSuc();
                },
                (error) => {
                    if (generation !== sessionGeneration) return;
                    onErr(error);
                    handleSnackbarError(error);
                },
            );
        },
        initiateMfaVerificationForPhone: (onSuc, onErr) => {
            const { countryCode, areaCode, phoneNumber } = get();
            const generation = sessionGeneration;
            mfaVerification(
                useRegistrationSessionStore.getState().userId,
                {
                    action: 'verify-phone' as any,
                    new_phone_number: `${countryCode || '1'}${areaCode}${phoneNumber}`,
                },
                (response: MfaVerificationResponse) => {
                    if (generation !== sessionGeneration) return;
                    if (response.mfa_method === 'sms') {
                        onSuc();
                    } else {
                        onErr();
                    }
                },
                () => {
                    if (generation !== sessionGeneration) return;
                    onErr();
                },
            );
        },
        verifyPhone: (code, onSuc, onErr) => {
            // A late response can hand the caller the previous applicant's
            // duplicate-account email and navigate this tab on it.
            const generation = sessionGeneration;
            verifyPhoneTransport(
                useRegistrationSessionStore.getState().userId,
                // Peeked, not fetched: the backend only screens this endpoint once
                // user.FinishedSignUp() is true, so during onboarding the key is unused —
                // and executeDigitalKyc still needs this 'kyc' session, which a fetch
                // would consume out from under it. Sent anyway so the screening works if
                // the number is changed from an already-onboarded account.
                { code, sardine_session_key: peekSardineSessionKey() ?? undefined },
                (response) => {
                    if (generation !== sessionGeneration) return;
                    if (response.duplicate_user_redacted_email) {
                        onSuc(response.duplicate_user_redacted_email);
                    } else {
                        onSuc();
                    }
                },
                (error) => {
                    if (generation !== sessionGeneration) return;
                    onErr(error);
                },
            );
        },
        endSession: () => {
            const { actions } = get();
            actions.clearStoredState();
            actions.dispose();
        },
        clearStoredState: () => {
            // Otherwise the next applicant's first trackKycStep() calls
            // ensureKycFlowId(), reuses the leftover id, and merges two
            // applicants' events into one KYC flow.
            clearKycFlowId();
            clearCsrfToken();
            useRegistrationSessionStore.getState().actions.userIdCleared();
            removeStoredItems('session', [
                EMAIL_SESSION_STORAGE_KEY,
                ...PRESIGNUP_RESUME_MARKER_KEYS,
                PRESIGNUP_TOKEN_SESSION_STORAGE_KEY,
                // VerifyEmail parks a magic-link code here while it strips the
                // code from the URL. The URL keeps `?user=<previous applicant>`,
                // so a leftover code lets a reload re-submit their verification
                // and hand the next person in this tab their application.
                'email_verification_code',
                // Duplicate-account/recovery data is another account's PII; it
                // seeds the store's initial state on every reload, so leaving it
                // behind would surface it to the next applicant in this tab.
                'recovery_duplicate_email',
                'recovery_duplicate_oauth_provider',
                'recovery_duplicate_account_is_recoverable',
                'recovery_reset_code',
                'recovery_existing_user_id',
            ]);
            removeStoredItems('local', [
                PREFILL_FIRST_NAME,
                PREFILL_LAST_NAME,
                PREFILL_DOB,
                PREFILL_PHONE_NUMBER,
                OAUTH_PREFILL_FIRST_NAME,
                OAUTH_PREFILL_LAST_NAME,
            ]);
        },
        dispose: () => {
            // Cancel and invalidate an in-flight profile load before resetting:
            // its success callback would otherwise repopulate the fields below
            // with the applicant whose session just ended.
            sessionGeneration += 1;
            if (profileFetchRef !== null) {
                profileFetchRef.cancel('profile request aborted on registration session teardown');
                profileFetchRef = null;
            }
            // The in-flight guard is only ever lowered by setUserAttribution's own
            // callbacks, and handleErrorCallback drops those entirely after a forced
            // disconnect. Left raised it survives for the life of the tab and makes
            // sendAttributionHistory a no-op for every later signup and login, so
            // release it with the rest of this module's request state.
            attributionSendInFlight = false;
            get().actions.disposeKyc();
            set({
                email: '',
                password: '',
                passwordConfirmation: '',
                sandboxEmailValidationCode: undefined,
                firstName: '',
                lastName: '',
                address1: '',
                address2: '',
                postalCode: '',
                taxId: '',
                city: '',
                state: '',
                country: '',
                birthdate: undefined,
                dayOfBirth: '',
                monthOfBirth: '',
                yearOfBirth: '',
                countryCode: '',
                areaCode: '',
                phoneNumber: '',
                rawPhoneInput: '',
                pendingSmsMarketingConsent: undefined,
                documentsReviewed: [false, false, false, false],
                agreementCheckboxes: [false],
                fundMethod: undefined,
                loading: false,
                loaded: false,
                referralSignupInfo: undefined,
                signupSardineSessionKey: '',
                prefillAttemptId: '',
                emailVerificationToken: '',
                presignupEmailVerificationEnabled: undefined,
            });
        },

        // --- kyc actions ---
        updateFromVo: (signup) => {
            set({
                documentVerificationStatus: signup.document_verification_status,
                documentVerificationResult: signup.document_verification_result,
                digitalVerificationStatus: signup.digital_verification_status,
                digitalVerificationResult: signup.digital_verification_result,
                activationStep: signup.current_activation_step,
                lastSignupStep: signup.last_signup_step,
            });
        },
        setSsnCollectionMode: (mode) => {
            set({ ssnCollectionMode: mode });
            setSsnStatusCache(mode);
        },
        setSignupAction: (action) => {
            set({ signupAction: action });
            // Sync prefill_attempt_id with the registration store so the subsequent
            // PUT /v1/users/{user_id} can include it. Clear it when the latest action
            // no longer represents a prefill step (e.g. after OTP failure/expiry
            // fallback) so a stale ID is never submitted with a manual profile.
            if (action?.prefill_attempt_id) {
                get().actions.setPrefillAttemptId(action.prefill_attempt_id);
            } else {
                get().actions.setPrefillAttemptId('');
            }
        },
        signUpAdvance: (payload, onSuc = noop, onErr = noop, onFinally = noop) => {
            const { actions } = get();
            // Guard against duplicate RiskOS start submissions from refresh or
            // double-clicks. We only refuse to send while a request is in flight;
            // once it completes (success or failure), follow-up advances are
            // allowed.
            if (get().advanceInFlight) {
                onFinally();
                return;
            }
            set({ advanceInFlight: true });
            const generation = sessionGeneration;
            kycFetchRef = signUpAdvance(
                useRegistrationSessionStore.getState().userId,
                payload,
                (response) => {
                    if (generation !== sessionGeneration) return;
                    actions.updateFromVo(response.signup);
                    actions.updateExtraFields({
                        signup: response.signup,
                        duplicate_account_is_recoverable: response.duplicate_account_is_recoverable,
                        duplicate_email: response.duplicate_email,
                        duplicate_oauth_provider: response.duplicate_oauth_provider,
                        duplicate_access_level: response.duplicate_access_level,
                    });
                    actions.setSignupAction(response.signup_action);
                    actions.setSsnCollectionMode(response.ssn_collection_mode);
                    void import('lib/stores/userSessionStore').then(({ setAccessLevel }) => {
                        // Rechecked inside the chunk load: the outer guard passed
                        // before it started, but a session that ends while it loads
                        // would otherwise have this write the ended applicant's role
                        // back to auth state and localStorage — over whoever logged
                        // in meanwhile.
                        if (generation !== sessionGeneration) return;
                        setAccessLevel(response.access_level);
                    });
                    onSuc(response);
                },
                (err) => {
                    if (generation !== sessionGeneration) return;
                    onErr(err);
                },
                () => {
                    if (generation !== sessionGeneration) return;
                    set({ advanceInFlight: false });
                    onFinally();
                },
            );
        },
        updateExtraFields: (response) => {
            if (response.duplicate_email) {
                trackKycStep('digitalDuplicatedEmail', {
                    kyc_step_group: 'verification',
                    submit_result: 'failed',
                    provider: 'socure',
                    verification_result: 'error',
                    error_code: 'duplicate_email',
                    error_source: 'risk',
                    retryable: false,
                });
                clearKycFlowId();
                get().actions.setDuplicateEmail(response.duplicate_email);
                setSessionStorageItem('recovery_duplicate_email', response.duplicate_email);
            }
            if (response.duplicate_oauth_provider) {
                trackKycStep('digitalDuplicatedEmail', {
                    kyc_step_group: 'verification',
                    submit_result: 'failed',
                    provider: 'socure',
                    verification_result: 'error',
                    error_code: 'duplicate_oauth_provider',
                    error_source: 'risk',
                    retryable: false,
                });
                clearKycFlowId();
                get().actions.setDuplicateOAuthProvider(response.duplicate_oauth_provider);
                setSessionStorageItem('recovery_duplicate_oauth_provider', response.duplicate_oauth_provider);
            }
            if (response.duplicate_access_level) {
                get().actions.setDuplicateAccessLevel(response.duplicate_access_level);
            }
            if (response.duplicate_account_is_recoverable !== undefined) {
                set({ duplicateAccountIsRecoverable: response.duplicate_account_is_recoverable });
                setSessionStorageItem(
                    'recovery_duplicate_account_is_recoverable',
                    String(response.duplicate_account_is_recoverable),
                );
            }
            if (response.reason) {
                switch (response.reason) {
                    case 'id_expired':
                        trackKycStep('docvIdExpiredError', {
                            kyc_step_group: 'docv',
                            field_group: 'docv',
                            provider: 'socure',
                            docv_result: 'error',
                            error_code: response.reason,
                            error_source: 'provider',
                            retryable: true,
                        });
                        break;
                    case 'image_issue':
                        trackKycStep('docvImageIssueError', {
                            kyc_step_group: 'docv',
                            field_group: 'docv',
                            provider: 'socure',
                            docv_result: 'error',
                            error_code: response.reason,
                            error_source: 'provider',
                            retryable: true,
                        });
                        break;
                    case 'dob':
                        trackKycStep('docvDobError', {
                            kyc_step_group: 'docv',
                            field_group: 'dob',
                            provider: 'socure',
                            docv_result: 'error',
                            error_code: response.reason,
                            error_source: 'provider',
                            retryable: true,
                        });
                        break;
                    case 'name':
                        trackKycStep('docvNameError', {
                            kyc_step_group: 'docv',
                            field_group: 'name',
                            provider: 'socure',
                            docv_result: 'error',
                            error_code: response.reason,
                            error_source: 'provider',
                            retryable: true,
                        });
                        break;
                    default:
                        trackKycStep('docvError', {
                            kyc_step_group: 'docv',
                            field_group: 'docv',
                            provider: 'socure',
                            docv_result: 'error',
                            error_code: response.reason,
                            error_source: 'provider',
                            retryable: true,
                        });
                        break;
                }
                set({ reasonDocumentRetry: response.reason });
            } else if (get().reasonDocumentRetry !== 'profile_mismatch') {
                set({ reasonDocumentRetry: '' });
            }
            if (get().reasonDocumentRetry !== 'profile_mismatch') {
                set({ documentVerificationErrorMessage: '' });
            }
        },
        loadKyc: (polling = undefined, onSuc = noop, onErr = noop, onFinally = noop, isPoll = false, userId) => {
            const { actions } = get();
            set({ kycLoading: true });
            const generation = sessionGeneration;
            kycFetchRef = getUserSignup(
                !isPoll,
                userId ?? useRegistrationSessionStore.getState().userId,
                (signupResponse) => {
                    if (generation !== sessionGeneration) return;
                    actions.updateFromVo(signupResponse.signup);
                    actions.updateExtraFields({
                        signup: signupResponse.signup,
                        duplicate_account_is_recoverable: signupResponse.duplicate_account_is_recoverable,
                        duplicate_email: signupResponse.duplicate_email,
                        duplicate_oauth_provider: signupResponse.duplicate_oauth_provider,
                        duplicate_access_level: signupResponse.duplicate_access_level,
                    });
                    actions.setSignupAction(signupResponse.signup_action);
                    actions.setSsnCollectionMode(signupResponse.ssn_collection_mode);
                    void import('lib/stores/userSessionStore').then(({ setAccessLevel }) => {
                        // Rechecked inside the chunk load: the outer guard passed
                        // before it started, but a session that ends while it loads
                        // would otherwise have this write the ended applicant's role
                        // back to auth state and localStorage — over whoever logged
                        // in meanwhile.
                        if (generation !== sessionGeneration) return;
                        setAccessLevel(signupResponse.access_level);
                    });
                    if (!!polling) {
                        actions.startPolling(polling === 'slow');
                    }
                    onSuc(signupResponse.signup);
                },
                // Guarded like the others: disposeKyc() cancels this request after
                // bumping the generation, but handleErrorCallback still reports a
                // cancellation as an error. Callers act on it — Profile.tsx
                // schedules signup navigation, PrefillReview.tsx routes off the
                // (now reset) store — which must not happen after a logout.
                () => {
                    if (generation !== sessionGeneration) return;
                    onErr();
                },
                () => {
                    if (generation !== sessionGeneration) return;
                    set({ kycLoading: false });
                    onFinally();
                },
            );
        },
        executeDigitalKyc: async (socureDeviceSessionId, onSuc = noop, onErr = noop, onFinally = noop) => {
            set({ kycLoading: true });
            const { actions } = get();
            const generation = sessionGeneration;
            // deferDrift: skip drift logging in the fetch — waitForSardineBound records it
            // post-wait so a bind that lands during the wait isn't counted as a missing session.
            const sardine = await fetchSardineSessionKey('kyc', useRegistrationSessionStore.getState().userId, true);
            // Checked before the bind wait, not just after it: waitForSardineBound can hold
            // for 10s, and there is no reason to spend it on an ended session.
            if (!sardine.ok) {
                onFinally();
                return;
            }
            const sardineSessionKey = sardine.key;
            // Give an in-flight SDK bind a bounded chance to land before submitting, so the
            // backend's Sardine session has device data. Submits regardless of the result.
            if (sardineSessionKey) {
                await waitForSardineBound(sardineSessionKey, 'kyc');
            }

            // The Sardine waits above are unbounded from this function's point of
            // view; a logout can land during them, and firing the KYC submission
            // afterwards would run it against the ended session.
            if (generation !== sessionGeneration) {
                onFinally();
                return;
            }

            const payload: ExecuteKycRequest = {
                ...(socureDeviceSessionId ? { socure_device_session_id: socureDeviceSessionId } : {}),
                sardine_session_key: sardineSessionKey || undefined,
                riskified_cart_token: getRiskifiedSessionId() || undefined,
            };

            kycFetchRef = executeDigitalKyc(
                useRegistrationSessionStore.getState().userId,
                payload,
                (response) => {
                    if (generation !== sessionGeneration) return;
                    actions.updateFromVo(response.signup);
                    actions.updateExtraFields(response);
                    onSuc();
                },
                (err) => {
                    if (generation !== sessionGeneration) return;
                    onErr(err);
                },
                () => {
                    if (generation !== sessionGeneration) return;
                    set({ kycLoading: false });
                    onFinally();
                },
            );
        },
        setDuplicateEmail: (newValue) => set({ duplicateEmail: newValue }),
        setDuplicateOAuthProvider: (newValue) => set({ duplicateOAuthProvider: newValue }),
        setDuplicateAccessLevel: (newValue) => set({ duplicateAccessLevel: newValue }),
        setRecoveryParams: (code, existingUserId) => {
            set({ recoveryCode: code, recoveryExistingUserId: existingUserId });
            setSessionStorageItem('recovery_reset_code', code);
            setSessionStorageItem('recovery_existing_user_id', existingUserId);
        },
        clearRecoveryParams: () => {
            set({ recoveryCode: '', recoveryExistingUserId: '' });
            removeStoredItems('session', ['recovery_reset_code', 'recovery_existing_user_id']);
        },
        setIsConnectedOnMobile: (value) => set({ isConnectedOnMobile: value }),
        setClosedWhileConnected: (value) => {
            setSessionStorageItem(DOCUMENT_CLOSED_WHILE_CONNECTED, value ? 'true' : 'false');
            set({ closedWhileConnected: value });
        },
        setStartedSocure: (value) => set({ startedSocure: value }),
        setDisplaySocure: (value) => set({ displaySocure: value }),
        setReasonDocumentRetry: (value) => set({ reasonDocumentRetry: value }),
        setDocumentVerificationErrorMessage: (value) => set({ documentVerificationErrorMessage: value }),
        // v5: initialize socure mounting it in our UI using token-based launch
        initSocure: async () => {
            set({ displaySocure: true, socureLoadError: false });
            const { actions } = get();
            const generation = sessionGeneration;
            const authUser = useAuthStore.getState().user;
            const registrationCountry = get().country.trim().toUpperCase();
            const authCountry = (authUser?.country ?? '').trim().toUpperCase();
            const isCountryDomestic = (c: string): boolean =>
                c === '' || REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(c);
            const isDomestic = isCountryDomestic(registrationCountry) && isCountryDomestic(authCountry);
            const socureSdkKey = isDomestic ? config.socureSdkKey : config.socureInternationalSdkKey;

            try {
                // Step 1: fetch transaction token from backend
                const tokenResp = await exchangeApi.post<{ docv_transaction_token: string }>(
                    `/users/${useRegistrationSessionStore.getState().userId}/docv_token`,
                    {},
                );
                // A logout can land while /docv_token is in flight. resetSocure()
                // already ran by then, so restoring the token here — and letting
                // the launch below run — would reopen the ended applicant's
                // verification session for whoever is in this tab now.
                if (generation !== sessionGeneration) return false;
                set({ docvTransactionToken: tokenResp.data.docv_transaction_token });
                loadSocure(() => {
                    if (generation !== sessionGeneration) return;
                    trackKycStep('socureError', {
                        kyc_step_group: 'docv',
                        field_group: 'docv',
                        provider: 'socure',
                        docv_result: 'error',
                        error_code: 'socure_sdk_load_failed',
                        error_source: 'provider',
                        retryable: true,
                    });
                    set({ displaySocure: false, socureLoadError: true });
                });
            } catch {
                // Checked before the metric, not after: trackKycStep calls
                // ensureKycFlowId(), which would recreate the flow id the teardown
                // just cleared and merge this applicant's events with the next
                // one's.
                if (generation !== sessionGeneration) return false;
                trackKycStep('socureError', {
                    kyc_step_group: 'docv',
                    field_group: 'docv',
                    provider: 'socure',
                    docv_required: true,
                    docv_result: 'error',
                    error_code: 'docv_token_error',
                    error_source: 'backend',
                    retryable: true,
                });
                set({ displaySocure: false });
                return false;
            }

            // The SDK holds these for the lifetime of the launched session, so they
            // can fire long after a logout — and after another applicant has started
            // their own DocV in this tab. Unguarded, a stale onSuccess would overwrite
            // the live `deviceSessionToken` and submit the ended applicant's device
            // session against the current one's transaction.
            const socureConfig: SocureConfigV5 = {
                onError: () => {
                    if (generation !== sessionGeneration) return;
                    trackKycStep('socureError', {
                        kyc_step_group: 'docv',
                        field_group: 'docv',
                        provider: 'socure',
                        docv_started: true,
                        docv_result: 'error',
                        error_source: 'provider',
                        retryable: true,
                    });
                },
                onSuccess: (response) => {
                    if (generation !== sessionGeneration) return;
                    // Capture device session token if present
                    set({ deviceSessionToken: response.deviceSessionToken || null });
                    trackKycStep('socureSuccess', {
                        kyc_step_group: 'docv',
                        field_group: 'docv',
                        provider: 'socure',
                        docv_started: true,
                        docv_submitted: true,
                        docv_result: 'unknown',
                    });
                    const trackerPayload: FacebookPixelTrackerPayload = {
                        page: window.location.pathname,
                        event_id: uuidv4(),
                    };
                    // For Amplitude, this is replaced by recordSignUpMetric, but we still need it for GTM tags
                    analyticsProvider.sendEvent('SignupSocureSuccess', trackerPayload);
                    // Socure onSuccess only means the document was *submitted* — the verification
                    // decision isn't known yet. Run the backend check first, then resume the pending
                    // step-up flow (e.g. the deposit) only once the document is actually approved.
                    // On a rejected/resubmit result, leave the identity modal open so
                    // IdentityFlowContent can show the rejection/resubmit screen instead of silently
                    // closing and initiating a deposit the user can't complete.
                    const { docvTransactionToken } = get();
                    if (docvTransactionToken) {
                        actions.executeDocumentKyc(
                            docvTransactionToken,
                            () => {
                                if (get().documentVerificationResult === 'approved') {
                                    useIdentityFlowDialogStore.getState().actions.completed();
                                }
                            },
                            noop,
                            () => {
                                actions.resetSocure();
                            },
                        );
                    }
                },
                onProgress: (progress) => {
                    if (generation !== sessionGeneration) return;
                    if (progress.status === 'WAITING_FOR_UPLOAD') {
                        actions.setIsConnectedOnMobile(true);
                    }
                },
                qrCodeNeeded: true,
                disableSmsInput: true,
            };

            // Step 2: wait for bundle load, then launch v5 SDK
            const setStartedSocure = actions.setStartedSocure;
            socureExecuteOnLoad(() => {
                // The bundle load is unbounded; a logout in the meantime already
                // tore this session down, so don't launch the SDK for it.
                if (generation !== sessionGeneration) return;
                const socureSdk = (typeof window !== 'undefined' && (window as any).SocureDocVSDK) || undefined;
                const { docvTransactionToken } = get();
                if (socureSdk && docvTransactionToken) {
                    socureSdk
                        .launch(socureSdkKey, docvTransactionToken, '#embedDiv', socureConfig)
                        .then(() => {
                            if (generation !== sessionGeneration) return;
                            setStartedSocure(true);
                        })
                        .catch((err: any) => {
                            // A logout can reset Socure while the launch is still
                            // pending; the rejection that follows belongs to the
                            // ended session, so it must not raise a snackbar or a
                            // KYC error over whoever is in this tab now.
                            if (generation !== sessionGeneration) return;
                            useSnackbarStore.getState().actions.pushMessage({
                                text: `Error launching Socure SDK: ${err.message || 'Unknown error'}`,
                                variant: 'negative',
                            });
                            trackKycStep('socureError', {
                                kyc_step_group: 'docv',
                                field_group: 'docv',
                                provider: 'socure',
                                docv_result: 'error',
                                error_code: 'socure_sdk_launch_failed',
                                error_source: 'provider',
                                retryable: true,
                            });
                        });
                } else {
                    trackKycStep('socureError', {
                        kyc_step_group: 'docv',
                        field_group: 'docv',
                        provider: 'socure',
                        docv_result: 'error',
                        error_code: 'socure_sdk_unavailable',
                        error_source: 'provider',
                        retryable: true,
                    });
                    set({ displaySocure: false });
                }
            });

            return true;
        },
        resetDocumentVerification: () =>
            set({ documentVerificationStatus: 'pending', documentVerificationResult: 'unknown' }),
        // This invalidates the verification session in the mobile phone.
        resetSocure: () => {
            const { actions } = get();
            const sdk = (typeof window !== 'undefined' && (window as any).SocureDocVSDK) || undefined;
            try {
                sdk?.reset?.();
            } catch {
                // Third-party SDK. A failure to tear it down must not abort the
                // rest of this reset, which also runs as part of logout.
            }
            removeStoredItems('session', ['documentVerificationToken', 'publicApiKey']);
            removeStoredItems('local', ['devicer_id']);
            set({ docvTransactionToken: null, deviceSessionToken: null, socureLoadError: false });
            if (get().isConnectedOnMobile) {
                actions.setIsConnectedOnMobile(false);
                actions.setClosedWhileConnected(true);
            }
            actions.setStartedSocure(false);
            set({ displaySocure: false });
        },
        executeDocumentKyc: (documentId, onSuc = noop, onErr = noop, onFinally = noop) => {
            const { actions } = get();
            if (!get().submittingDocument) {
                trackKycStep('startDocv', {
                    kyc_step_group: 'docv',
                    field_group: 'docv',
                    provider: 'socure',
                    docv_started: true,
                    docv_submitted: true,
                });
                const trackerPayload: FacebookPixelTrackerPayload = {
                    page: window.location.pathname,
                    event_id: uuidv4(),
                };
                // TODO(hw/steven): deprecate, already covered by metrics.recordSignUpMetric
                analyticsProvider.sendEvent('SignupDocvStarted', trackerPayload);
                set({ submittingDocument: true });
                const generation = sessionGeneration;
                kycFetchRef = executeDocumentVerification(
                    useRegistrationSessionStore.getState().userId,
                    {
                        docv_transaction_token: documentId,
                        device_session_token: get().deviceSessionToken || undefined,
                        riskified_cart_token: getRiskifiedSessionId() || undefined,
                    },
                    (kycResult) => {
                        if (generation !== sessionGeneration) return;
                        actions.updateFromVo(kycResult.signup);
                        actions.updateExtraFields(kycResult);
                        onSuc();
                    },
                    (err) => {
                        if (generation !== sessionGeneration) return;
                        trackKycStep('docvRejected', {
                            kyc_step_group: 'docv',
                            field_group: 'docv',
                            provider: 'socure',
                            verification_result: 'rejected',
                            docv_result: 'rejected',
                            error_code: err.error?.code,
                            error_source: 'provider',
                            retryable: true,
                        });
                        const errorCode = err.error?.code?.toLowerCase();
                        if (errorCode === 'document_verification_profile_mismatch') {
                            set({
                                reasonDocumentRetry: 'profile_mismatch',
                                documentVerificationErrorMessage: err.error?.details || err.error?.message || '',
                            });
                            actions.loadProfile();
                            actions.loadKyc();
                            handleSnackbarError(err);
                        } else if (err.invalid) {
                            trackKycStep('docvError', {
                                kyc_step_group: 'docv',
                                field_group: 'docv',
                                provider: 'socure',
                                docv_result: 'error',
                                error_code: err.error?.code,
                                error_source: 'validation',
                                retryable: true,
                            });
                            useSnackbarStore.getState().actions.pushMessage({
                                text: 'Invalid input(s), please review the information provided.',
                                variant: 'negative',
                            });
                        } else {
                            handleSnackbarError(err);
                        }
                        onErr(err);
                    },
                    () => {
                        if (generation !== sessionGeneration) return;
                        set({ submittingDocument: false });
                        onFinally();
                    },
                );
            }
        },
        startPolling: (slow = true) => {
            if (!get().polling) {
                set({ polling: true });
                /*
                  Important to use a very low interval for the kyc status retrieval here because
                  users will try to restart the kyc flow if they don't receive any indication that it worked.
                 */
                kycTimer = setInterval(
                    () => get().actions.loadKyc(undefined, noop, noop, noop, true),
                    slow ? config.slowPollingInterval : 1000,
                );
            }
        },
        stopPolling: () => {
            if (get().kycLoading) {
                if (kycFetchRef !== null) {
                    kycFetchRef.cancel('get kyc data request aborted on kyc store disposal');
                    kycFetchRef = null;
                }
                set({ kycLoading: false });
            }
            if (get().polling) {
                if (kycTimer !== null) {
                    clearInterval(kycTimer);
                    kycTimer = null;
                }
                set({ polling: false });
            }
        },
        disposeKyc: () => {
            const { actions } = get();
            actions.stopPolling();
            // stopPolling() only cancels the shared request while `kycLoading` is
            // set; signUpAdvance() and executeDocumentKyc() track `advanceInFlight`
            // and `submittingDocument` instead, so theirs would survive it.
            if (kycFetchRef !== null) {
                kycFetchRef.cancel('kyc request aborted on registration session teardown');
                kycFetchRef = null;
            }
            // A logout that lands mid-DocV has to end the verification session
            // too: resetSocure() resets the SDK, invalidates the mobile hand-off
            // and drops the docv/device tokens. Without it the next signup in
            // this tab could re-render the previous applicant's Socure flow or
            // receive its callbacks.
            actions.resetSocure();
            // resetSocure() doesn't reach this independent store, so the step-up
            // modal stayed mounted with the previous account's callback: a later
            // verification would run it — e.g. reopening a deposit with parameters
            // captured for that account.
            useIdentityFlowDialogStore.getState().actions.dismissed();
            removeStoredItems('session', [DOCUMENT_CLOSED_WHILE_CONNECTED]);
            set({
                // resetSocure() raises `closedWhileConnected` when a mobile
                // hand-off was live — that notice belongs to the applicant who
                // just left, not to whoever signs up next.
                closedWhileConnected: false,
                startedSocure: false,
                displaySocure: false,
                documentVerificationStatus: 'pending',
                documentVerificationResult: 'unknown',
                digitalVerificationStatus: 'pending',
                digitalVerificationResult: 'unknown',
                // updateExtraFields() deliberately preserves a 'profile_mismatch'
                // reason when the next KYC response carries none, so leaving these
                // set would route the next applicant back into the profile flow
                // and show them the previous one's backend error details.
                reasonDocumentRetry: '',
                documentVerificationErrorMessage: '',
                // Back to the store's own initial value: selectApproved /
                // selectIsRejected read this, so a leftover 'kyc-rejected' would
                // decide the next applicant's screen.
                lastSignupStep: 'account-created',
                activationStep: 'verify-email',
                signupAction: undefined,
                advanceInFlight: false,
                submittingDocument: false,
                isConnectedOnMobile: false,
                // Recovery data identifies the pre-existing account a signup
                // collided with — drop it with the rest of the session so it
                // can't be read back by whoever signs up next in this tab.
                duplicateEmail: '',
                duplicateOAuthProvider: '',
                duplicateAccessLevel: '',
                duplicateAccountIsRecoverable: false,
                recoveryCode: '',
                recoveryExistingUserId: '',
            });
        },
    },
}));

export const displayApplicationIncomplete = (email: string): void => {
    const { pushMessage, popMessage } = useSnackbarStore.getState().actions;
    const toastId = pushMessage(
        {
            text: 'Your application is incomplete, click this message to receive an email with a link to resume your sign-up.',
            variant: 'neutral',
            persistent: true,
        },
        {
            onClick: (): void => {
                popMessage(toastId);
                useRegistrationStore.getState().actions.sendResumeLink(email, () => {
                    pushMessage({
                        text: 'You can close this window and resume your sign-up using the e-mail link.',
                        variant: 'positive',
                        persistent: true,
                    });
                });
            },
        },
    );
};

export const selectApproved = (state: RegistrationState): boolean => state.lastSignupStep === 'kyc-approved';
export const selectDigitalRejected = (state: RegistrationState): boolean =>
    state.digitalVerificationResult === 'rejected';
export const selectIsRejected = (state: RegistrationState): boolean => state.lastSignupStep === 'kyc-rejected';
export const selectDocumentRequired = (state: RegistrationState): boolean =>
    state.lastSignupStep === 'kyc-documents-requested';
export const selectFinished = (state: RegistrationState): boolean => state.activationStep === 'none';
export const selectDocumentReviewNeeded = (state: RegistrationState): boolean =>
    state.activationStep === 'review-document';
export const selectShouldResubmitDocument = (state: RegistrationState): boolean =>
    state.documentVerificationResult === 'resubmit-needed';
export const selectDocumentIsDecided = (state: RegistrationState): boolean =>
    state.documentVerificationResult === 'approved' || state.documentVerificationResult === 'rejected';
export const selectDocumentStepFinished = (state: RegistrationState): boolean =>
    state.documentVerificationStatus === 'finished' && selectDocumentIsDecided(state);
export const selectRunningDocumentVerification = (state: RegistrationState): boolean =>
    state.submittingDocument || state.documentVerificationStatus === 'running';
export const selectHasProfileReasonRetry = (state: RegistrationState): boolean =>
    state.reasonDocumentRetry === 'dob' ||
    state.reasonDocumentRetry === 'name' ||
    state.reasonDocumentRetry === 'profile_mismatch';
export const selectHasDocResubmitReasonRetry = (state: RegistrationState): boolean =>
    state.reasonDocumentRetry === 'id_expired' || state.reasonDocumentRetry === 'image_issue';
export const selectHasDuplicateInfo = (state: RegistrationState): boolean =>
    !!state.duplicateEmail || !!state.duplicateOAuthProvider;

export const selectPhoneDefaultCountry = (state: RegistrationState): CountryCode =>
    (state.detectedCountryCode || 'US') as CountryCode;

export const selectPhoneNumberWithSeparators = (state: RegistrationState): string => {
    try {
        // Use libphonenumber to format the phone number properly
        const parsed = parsePhoneNumberWithError(state.rawPhoneInput, selectPhoneDefaultCountry(state));
        return parsed.formatInternational();
    } catch {
        // Fallback to basic E.164 format if parsing fails
        return `+${state.countryCode} ${state.areaCode} ${state.phoneNumber}`;
    }
};

export const selectFullAddress = (state: RegistrationState): string =>
    `${state.address1}${state.address2 ? `, ` + state.address2 : ``}, ${state.city}, ${state.state}${
        state.postalCode ? ' ' + state.postalCode : ''
    }`;

export const selectValidationError = (state: RegistrationState): boolean => !state.email || !state.password;

// Server-directed RiskOS Advanced Prefill takes precedence over the legacy
// activation-step routing. Only the backend can put a user into this flow —
// never branch on a client-side flag.
export const selectSignUpActionPath = (state: RegistrationState): string | undefined => {
    const action = state.signupAction;
    if (isRiskosIdentityPrefillOtp(action)) {
        return '/sign-up/prefill-verify';
    }
    if (isRiskosIdentityPrefillSsn4(action)) {
        return '/sign-up/prefill-ssn4';
    }
    if (isRiskosIdentityPrefillStart(action)) {
        return '/sign-up/prefill';
    }
    if (isRiskosProfileReview(action)) {
        return '/sign-up/prefill-review';
    }
    return undefined;
};

export const selectProfileEntryPath = (state: RegistrationState): string => {
    const actionPath = selectSignUpActionPath(state);
    if (actionPath) {
        return actionPath;
    }
    // If user already has a phone number, they've verified it, skip to profile
    const hasPhoneNumber = !!useAuthStore.getState().user?.phone_number;
    if (hasPhoneNumber) {
        return '/sign-up/profile';
    }
    // Phone number step first
    return '/sign-up/phone-number';
};

export const selectNextSignupStep = (state: RegistrationState): string => {
    const actionPath = selectSignUpActionPath(state);
    if (actionPath) {
        return actionPath;
    }

    // For every other server-directed action, the signup_action.kind is the
    // source of truth and takes priority over the legacy current_activation_step
    // field. Each kind maps onto an existing signup path per the routing contract.
    const { signupAction, activationStep, duplicateAccountIsRecoverable, documentVerificationStatus, lastSignupStep } =
        state;
    const approved = selectApproved(state);
    const isRejected = selectIsRejected(state);
    const digitalRejected = selectDigitalRejected(state);
    const hasProfileReasonRetry = selectHasProfileReasonRetry(state);
    const hasDocResubmitReasonRetry = selectHasDocResubmitReasonRetry(state);
    if (signupAction?.kind) {
        switch (signupAction.kind) {
            case 'collect_phone':
                return '/sign-up/phone-number';
            case 'submit_profile':
                return selectProfileEntryPath(state);
            case 'submit_ssn':
                // The Profile step orchestrates SSN entry as a sub-step.
                return '/sign-up/profile';
            case 'digital_kyc':
                // Digital KYC has no dedicated UI — the Profile step
                // triggers it automatically after the user finishes
                // entering their info.
                return '/sign-up/profile';
            case 'document_kyc':
                return '/sign-up/identity';
            case 'manual_review':
                if (duplicateAccountIsRecoverable) {
                    return '/sign-up/duplicate-account';
                }
                return '/sign-up/review-needed';
            case 'rejected':
                if (duplicateAccountIsRecoverable) {
                    return '/sign-up/duplicate-account';
                }
                return '/sign-up/rejected';
            case 'done':
                if (approved) {
                    return '/sign-up/banking';
                }
                // Fall through to legacy routing for non-approved 'done' states.
                break;
        }
    }

    // Legacy activation_step routing — fallback for clients whose backend
    // hasn't been updated to return signup_action yet, and for 'done' actions
    // that need richer outcome handling.
    let nextPage = '/';

    switch (activationStep) {
        case 'verify-email': {
            nextPage = '/sign-up/verify';
            break;
        }
        case 'submit-profile': {
            nextPage = selectProfileEntryPath(state);
            break;
        }
        case 'submit-document': {
            nextPage = '/sign-up/identity';
            break;
        }
        case 'fund-account':
        case 'link-bank': {
            nextPage = '/sign-up/banking';
            break;
        }
        case 'review-document': {
            if (hasProfileReasonRetry) {
                nextPage = '/sign-up/profile';
            } else if (hasDocResubmitReasonRetry) {
                nextPage = '/sign-up/identity';
            } else {
                nextPage = '/sign-up/review-needed';
            }
            break;
        }
        case 'trade':
        // falls through
        case 'none': {
            // none means there's nothing more for the applicant to do
            if (approved) {
                nextPage = '/sign-up/banking';
            } else if (duplicateAccountIsRecoverable) {
                nextPage = '/sign-up/duplicate-account';
            } else if (isRejected) {
                nextPage = '/sign-up/rejected';
            } else if (digitalRejected || lastSignupStep === 'kyc-manual-review-requested') {
                // A 'rejected' digital result that has NOT reached the terminal 'kyc-rejected' step
                // (e.g. still in manual review) is pending a human decision, not a terminal decline.
                nextPage = '/sign-up/review-needed';
            } else {
                if (documentVerificationStatus === 'pending') {
                    nextPage = '/sign-up/identity';
                }
            }
            break;
        }
    }
    return nextPage;
};
